import { useState, useEffect, useCallback } from "react";
import { GraduationCap } from "lucide-react";
import api from "../../lib/api-client";
import { useGlobalToast } from "../../contexts/ToastContext";

/**
 * Superadmin: activar/desactivar el módulo "Clases" (add-on a medida) por
 * sede. Mismo espíritu que "Visibilidad en la app", reducido al mínimo.
 */

interface ModuleLocation {
  id: string;
  name: string;
  classesEnabled: boolean;
  organization: { id: string; name: string };
}

export default function AdminClassesModule() {
  const { showToast } = useGlobalToast();
  const [locations, setLocations] = useState<ModuleLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/classes-module");
      setLocations(response.data.data || []);
    } catch {
      showToast("No se pudieron cargar las sedes", "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleToggle = async (loc: ModuleLocation) => {
    setTogglingId(loc.id);
    try {
      await api.patch(`/admin/locations/${loc.id}/classes-enabled`, {
        enabled: !loc.classesEnabled,
      });
      setLocations((prev) =>
        prev.map((l) =>
          l.id === loc.id ? { ...l, classesEnabled: !loc.classesEnabled } : l,
        ),
      );
      showToast(
        !loc.classesEnabled
          ? `Módulo de clases activado en ${loc.name}`
          : `Módulo de clases desactivado en ${loc.name}`,
        "success",
      );
    } catch {
      showToast("No se pudo cambiar el módulo", "error");
    } finally {
      setTogglingId(null);
    }
  };

  const normalized = search.trim().toLowerCase();
  const filtered = normalized
    ? locations.filter(
        (l) =>
          l.name.toLowerCase().includes(normalized) ||
          l.organization.name.toLowerCase().includes(normalized),
      )
    : locations;

  // Agrupar por organización conservando el orden del backend
  const groups: Array<{ org: ModuleLocation["organization"]; items: ModuleLocation[] }> = [];
  for (const loc of filtered) {
    const last = groups[groups.length - 1];
    if (last && last.org.id === loc.organization.id) {
      last.items.push(loc);
    } else {
      groups.push({ org: loc.organization, items: [loc] });
    }
  }

  const enabledCount = locations.filter((l) => l.classesEnabled).length;

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <GraduationCap className="w-6 h-6 text-primary-600" />
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
              Módulo Clases
            </h1>
            <p className="text-sm text-gray-500">
              Add-on a medida · activo en {enabledCount}{" "}
              {enabledCount === 1 ? "sede" : "sedes"}
            </p>
          </div>
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar organización o sede…"
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-full sm:w-72"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <div
              key={group.org.id}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 font-semibold text-sm text-gray-700">
                {group.org.name}
              </div>
              {group.items.map((loc) => (
                <div
                  key={loc.id}
                  className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0"
                >
                  <span className="text-sm text-gray-800 min-w-0 truncate">
                    {loc.name}
                  </span>
                  <button
                    onClick={() => handleToggle(loc)}
                    disabled={togglingId === loc.id}
                    className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 disabled:opacity-50 ${
                      loc.classesEnabled ? "bg-green-500" : "bg-gray-300"
                    }`}
                    title={
                      loc.classesEnabled
                        ? "Desactivar el módulo en esta sede"
                        : "Activar el módulo en esta sede"
                    }
                  >
                    <span
                      className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                        loc.classesEnabled ? "right-0.5" : "left-0.5"
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
